import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Switch } from "@/components/ui/Switch";

type RecordScheduleControlProps<TValue extends string> = {
  id: string;
  label: string;
  enabled: boolean;
  disabled: boolean;
  amount: number;
  amountLabel: string;
  selectedValue: TValue;
  options: Array<{ value: TValue; label: string }>;
  onEnabledChange: (enabled: boolean) => void;
  onAmountChange: (amount: number) => void;
  onValueChange: (value: TValue) => void;
};

export function RecordScheduleControl<TValue extends string>({
  id,
  label,
  enabled,
  disabled,
  amount,
  amountLabel,
  selectedValue,
  options,
  onEnabledChange,
  onAmountChange,
  onValueChange,
}: RecordScheduleControlProps<TValue>) {
  const controlDisabled = disabled || !enabled;

  return (
    <div className="space-y-3 rounded-md border border-softiq-primary/10 bg-softiq-secondary-shade-100/35 px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="flex min-h-8 items-center justify-between gap-4">
        <Label htmlFor={id}>{label}</Label>
        <Switch
          id={id}
          checked={enabled}
          disabled={disabled}
          onCheckedChange={onEnabledChange}
        />
      </div>

      <div
        className={`flex justify-center gap-3 transition-opacity ${
          enabled ? "" : "opacity-45"
        }`}
      >
        <Input
          type="number"
          min={1}
          max={99}
          aria-label={amountLabel}
          value={amount}
          className="h-8 w-16 justify-self-center text-center"
          disabled={controlDisabled}
          onChange={(event) =>
            onAmountChange(Math.max(1, Number(event.target.value) || 1))
          }
        />
        <div
          className="inline-flex h-8 items-center rounded-full border border-softiq-primary/10 bg-white/70 px-0.5 dark:border-white/10 dark:bg-white/[0.06]"
          aria-label={amountLabel}
        >
          {options.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant="unstyled"
              disabled={controlDisabled}
              className={`h-7 rounded-full px-3 py-0 text-[10px] ${
                selectedValue === option.value
                  ? "bg-softiq-primary text-white dark:bg-softiq-secondary dark:text-softiq-primary"
                  : "text-softiq-primary/55 hover:bg-softiq-secondary-shade-100/70 dark:text-white/55 dark:hover:bg-white/10"
              }`}
              onClick={() => onValueChange(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
